import { useCallback, useEffect, useRef, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { config } from '@/config';
import { getZoomSignature } from './signature';

export interface ZoomMeetingConfig {
  meetingNumber: string;
  password?: string;
  userName: string;
  userEmail?: string;
  /** 0 = participant, 1 = host */
  role?: number;
}

export type ZoomMeetingStatus = 'idle' | 'initializing' | 'joining' | 'joined' | 'error' | 'left';

export interface ZoomMeetingControls {
  status: ZoomMeetingStatus;
  error: string | null;
  join: (cfg: ZoomMeetingConfig) => Promise<void>;
  leave: () => Promise<void>;
  toggleMute: (muted: boolean) => Promise<void>;
}

type ZoomClient = typeof import('@zoom/meetingsdk').ZoomMtg;

function toErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null) {
    return (err as Record<string, unknown>).reason as string
      ?? (err as Record<string, unknown>).message as string
      ?? JSON.stringify(err);
  }
  return String(err);
}

function showClientViewRoot(): void {
  const root = document.getElementById('zmmtg-root');
  if (root) {
    root.style.display = 'block';
  }
}

function hideClientViewRoot(): void {
  const root = document.getElementById('zmmtg-root');
  if (root) {
    root.style.display = 'none';
  }
}

function initZoomClient(ZoomMtg: ZoomClient): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.init({
      leaveUrl: window.location.href,
      patchJsMedia: true,
      disableInvite: true,
      disableCallOut: true,
      disableRecord: true,
      disablePreview: true,
      disableZoomLogo: true,
      isSupportChat: false,
      isSupportQA: false,
      isSupportPolling: false,
      isSupportBreakout: false,
      meetingInfo: [],
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}

function joinZoomClient(ZoomMtg: ZoomClient, cfg: ZoomMeetingConfig, signature: string): Promise<void> {
  return new Promise((resolve, reject) => {
    ZoomMtg.join({
      signature,
      meetingNumber: cfg.meetingNumber,
      passWord: cfg.password ?? '',
      userName: cfg.userName,
      userEmail: cfg.userEmail,
      success: () => resolve(),
      error: (err: unknown) => reject(err),
    });
  });
}

function leaveZoomClient(ZoomMtg: ZoomClient): Promise<void> {
  return new Promise((resolve) => {
    ZoomMtg.leaveMeeting({
      confirm: false,
      success: () => resolve(),
      error: () => resolve(),
    });
  });
}

export function useZoomMeeting(): ZoomMeetingControls {
  const medplum = useMedplum();
  const clientRef = useRef<ZoomClient | null>(null);
  const [status, setStatus] = useState<ZoomMeetingStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (clientRef.current) {
        void leaveZoomClient(clientRef.current);
      }
      hideClientViewRoot();
    };
  }, []);

  const join = useCallback(
    async (cfg: ZoomMeetingConfig) => {
      try {
        setStatus('initializing');
        setError(null);

        const { ZoomMtg } = await import('@zoom/meetingsdk');
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();
        showClientViewRoot();

        setStatus('joining');
        const signature = await getZoomSignature(medplum, cfg.meetingNumber, cfg.role ?? 1);
        await initZoomClient(ZoomMtg);
        await joinZoomClient(ZoomMtg, cfg, signature);

        clientRef.current = ZoomMtg;
        setStatus('joined');
      } catch (err) {
        const message = toErrorMessage(err);
        console.error('[Zoom] join error:', err);
        setError(message);
        setStatus('error');
        hideClientViewRoot();
      }
    },
    [medplum],
  );

  const leave = useCallback(async () => {
    if (clientRef.current) {
      await leaveZoomClient(clientRef.current);
      hideClientViewRoot();
      setStatus('left');
    }
  }, []);

  const toggleMute = useCallback(async (_muted: boolean) => {
    // Client View does not expose a reliable self-mute helper without the current participant id.
  }, []);

  return { status, error, join, leave, toggleMute };
}

export function isZoomEnabled(): boolean {
  return config.zoom.enabled;
}
